import type { LobbyCharacterInfo } from '@danda/shared';
import type { Character } from '@/lib/stores/lobbyStore';

export interface DuelParticipantInput {
  charId?: string;
  name?: string;
  imageUrl?: string;
}

export interface DuelParticipants {
  player?: DuelParticipantInput;
  opponent?: DuelParticipantInput;
}

/**
 * Resolve a single duel participant's portrait URL against the live lobby
 * store. Mirrors `GameLayout.resolveUrl`. Order of preference:
 *   1. Own character `portraitUrl`
 *   2. Own character `session.portrait_url` (latest WS-hydrated value)
 *   3. Own character `avatarUrl`
 *   4. Lobby roster `portraitUrl`
 *   5. Lobby roster `avatarUrl`
 *   6. Server-supplied fallback (from the raw transition snapshot)
 *
 * Exported separately from LobbyLayout.tsx so the unit test can import without
 * pulling React + the lobby component graph.
 */
export function resolveDuelPortraitUrl(
  charId: string | undefined,
  fallback: string | undefined,
  ownCharacters: Character[],
  lobbyCharacters: LobbyCharacterInfo[]
): string | undefined {
  if (!charId) return fallback;
  const own = ownCharacters.find((c) => c.id === charId);
  const lobby = lobbyCharacters.find((c) => c.id === charId);
  return (
    own?.portraitUrl ??
    own?.session?.portrait_url ??
    own?.avatarUrl ??
    lobby?.portraitUrl ??
    lobby?.avatarUrl ??
    fallback
  );
}

/**
 * Build the resolved duel participants object for the loader. Re-runs whenever
 * `participants`, `ownCharacters`, or `lobbyCharacters` change so that a
 * `lobby:session-state` arrival mid-transition flows through.
 */
export function resolveDuelParticipants(
  participants: DuelParticipants | undefined,
  ownCharacters: Character[],
  lobbyCharacters: LobbyCharacterInfo[]
): DuelParticipants | undefined {
  if (!participants) return undefined;
  return {
    player: participants.player
      ? {
          ...participants.player,
          imageUrl: resolveDuelPortraitUrl(
            participants.player.charId,
            participants.player.imageUrl,
            ownCharacters,
            lobbyCharacters
          ),
        }
      : undefined,
    opponent: participants.opponent
      ? {
          ...participants.opponent,
          imageUrl: resolveDuelPortraitUrl(
            participants.opponent.charId,
            participants.opponent.imageUrl,
            ownCharacters,
            lobbyCharacters
          ),
        }
      : undefined,
  };
}
